import Ember from 'ember';
import { restartableTask } from 'ember-concurrency';
import { StockBlockItemStats, TItemStat } from 'volta/models/app-feature';
import { commonOrUndefined } from 'volta/utils/array-utils';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { sum } from 'volta/utils/math-utils';

import { sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { ISkuWorkshopCapacitySimple } from 'volta/models/types/workshop';
import type IntlService from 'ember-intl/services/intl';
import type AppSettings from 'volta/services/app-settings';
import type EventStreamService from 'volta/services/event-stream';
import type StoreService from 'volta/services/store';

interface IArgs {
  skuId: string;
  unitMeasure?: string;
  hideHeader?: boolean;
  hideFooter?: boolean;
  itemStats?: TItemStat[];
  close?: () => void;
  switchTab?: () => void;
  showOpenOrders?: () => void;
  showDetailsModal?: () => void;
}

export default class SkuWorkshopsCapacity extends Component<IArgs> {
  // Services
  // ~~~~~

  @service declare store: StoreService;
  @service declare intl: IntlService;
  @service declare eventStream: EventStreamService;
  @service declare appSettings: AppSettings;

  // Properties
  // ~~~~~
  @tracked rows: ISkuWorkshopCapacitySimple[] = [];
  @tracked sortProperties = ['workshop.code:asc'];
  @tracked filter?: string;

  get itemStats() {
    return this.args.itemStats ?? StockBlockItemStats;
  }

  // Computed Properties
  // ~~~~~

  @sort('rows', 'sortProperties')
  declare sortedRows: ISkuWorkshopCapacitySimple[];

  @cached
  get finalRows() {
    const { sortedRows, filter } = this;
    return sortedRows
      .filter(
        (r) =>
          !filter ||
          filter === '' ||
          (r.workshop && `${r.workshop.code}${r.workshop.description}`.toLowerCase().includes(filter.toLowerCase()))
      )
      .map((row) => {
        const onHand = (row.acceptedOnHand ?? 0) + (row.rejectedOnHand ?? 0);
        const capacity = row.maxThreshold ?? row.capacity;
        const maxValue = Math.max(capacity ?? 0, row.minThreshold ?? 0);
        const level = maxValue > 0 ? onHand / maxValue : 1;
        const criticality =
          (capacity && capacity > 0 && onHand >= capacity) || onHand < (row.minThreshold ?? 0)
            ? 'high'
            : 'low';
        const capacityOrOnHand = capacity ?? onHand;
        const minThresholdPercent = row.minThreshold
          ? capacityOrOnHand === 0
            ? 0
            : (row.minThreshold / capacityOrOnHand) * 100
          : undefined;
        return {
          ...row,
          capacity,
          level,
          criticality,
          minThresholdPercent,
          id: `${row.sku.skuId}_${row.workshop.workshopId}`
        };
      });
  }

  get totals() {
    const { finalRows } = this;
    return {
      capacityUnit: commonOrUndefined(finalRows.map(r => r.capacityUnit)),
      acceptedOnHand: sum(finalRows.map(r => r.acceptedOnHand) ?? 0),
      rejectedOnHand: sum(finalRows.map(r => r.rejectedOnHand) ?? 0),
      minThreshold: sum(finalRows.map(r => r.minThreshold ?? 0)),
      capacity: sum(finalRows.map(r => r.capacity ?? 0))
    };
  }

  // Lifecycle Hooks
  // ~~~~~

  // Private Callbacks/Handlers
  // ~~~~~

  getWorkshopsCapacity = restartableTask(async (skuId: string, workshops?: string[]) => {
    try {
      const response: Ember.Array<ISkuWorkshopCapacitySimple> = await this.store.customQuery(
        'sku',
        'workshopsCapacitySimple',
        skuId,
        { workshops: workshops?.join(',') }
      );
      if (response && response.length) {
        this.rows = response.toArray();
      } else {
        this.rows = [];
      }
    } catch (error) {
      defaultErrorHandler(error);
    }
  });
}
